//
// Full outcome ratings chart — ported and adapted from takso-poc OutcomeRatings.jsx.
//
// Features:
//   • Heading area: evaluation plan count, participant count, response count
//   • Methodology checkboxes (filter by distinct methodology)
//   • Area chart showing per-activity rating distributions (Trend — Rate of Change)
//   • Individual rate-of-change scatter chart for prePost outcomes (Individual — Rate of Change)
//   • Prose summary
//
// Props:
//   outcomeId    — number
//   outcomeName  — string (fallback title)
//   programIds   — number[] — filter to these parent programs
//   orgId        — number | null — org scope when no programIds
//   filterParams — object — platform-wide filter params (statuses, type, manager, dates)
//   onClose      — () => void — close button handler

/* eslint-disable react-hooks/exhaustive-deps -- the fetch effect intentionally
   depends on joined/derived values rather than the raw arrays/objects, to
   avoid refetching on every unrelated parent re-render. */

import React, {
  Fragment,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';

import { useTranslation, Trans } from 'react-i18next';
import html2canvas from 'html2canvas';

import { reportBuilderGet } from '../../lib/chartApi';
import { displayOutcome } from '../../lib/outcomeDisplayLabels';
import { LocaleService } from '../../services';

// ── Colour constants ──────────────────────────────────────────────────────────
const BASE_COLOR = '222,72,12'; // warm red-orange — post
const PRE_COLOR = '47,128,237'; // #2F80ED — blue — pre
const QUICK_RESPONSE_COLOR = '107,114,128'; // grey — Quick Response is post-only, no pre/post pair
const AXIS_COLOR = '#9ca3af';
const LABEL_COLOR = '#374151';

// An activity's area is coloured grey only when every plan contributing to it
// is Quick Response — mixed-method activities keep the standard post colour.
function isQuickResponseOnly(activity) {
  const plans = activity?.plans;
  return (
    Array.isArray(plans) &&
    plans.length > 0 &&
    plans.every((p) => p.method_id === 'Quick Response')
  );
}

// ── Chart layout constants ────────────────────────────────────────────────────
const CHART_MAX_PX = 860;
const PAD_LEFT = 66;
const Y_COL_X = Math.round(PAD_LEFT / 3);
const PAD_RIGHT = 24;
const PAD_TOP = 20;
const PAD_BOTTOM = 58;
const PHI = (1 + Math.sqrt(5)) / 2; // golden ratio ≈ 1.618

const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Third-party assessment methods — where someone other than the participant rates.
const THIRD_PARTY_RE = /manager|pka|proxy|expert/i;
// Self-assessed methods — for prose commentary.
const SELF_ASSESSED_RE = /self|manager|pka/i;

// ── Utility functions ─────────────────────────────────────────────────────────

const fmtN = (n) => (n == null ? '' : LocaleService.fmtLocaleNumeral(n));

// Per-area opacity = 0.5 / √N  (square-root decay)
function areaOpacityFor(count) {
  return Math.max(0.01, 0.5 / Math.sqrt(count));
}

// Returns true when the distribution has a clear single interior peak — bell-shaped.
function isBellShaped(agg) {
  const maxVal = Math.max(...agg);
  if (maxVal === 0) return false;
  const peakIdx = agg.indexOf(maxVal);
  if (peakIdx < 2 || peakIdx > 7) return false;
  let leftViolations = 0;
  for (let i = 1; i <= peakIdx; i++) {
    if (agg[i] < agg[i - 1]) leftViolations++;
  }
  let rightViolations = 0;
  for (let i = peakIdx + 1; i < 10; i++) {
    if (agg[i] > agg[i - 1]) rightViolations++;
  }
  return leftViolations <= 1 && rightViolations <= 1;
}

// Build a smooth filled area SVG path for one distribution (cubic Bezier).
function buildPath(distribution, xScale, yScale, chartHeight, padTop) {
  if (!distribution || distribution.length !== 10) return '';
  const pts = distribution.map((n, i) => [
    xScale(i),
    padTop + chartHeight - yScale(n),
  ]);
  const baseline = padTop + chartHeight;
  let d = `M ${pts[0][0]} ${baseline} L ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const dx = x1 - x0;
    d += ` C ${x0 + dx / 3} ${y0}, ${x1 - dx / 3} ${y1}, ${x1} ${y1}`;
  }
  d += ` L ${pts[9][0]} ${baseline} Z`;
  return d;
}

// Split a label at the word boundary nearest the middle when it exceeds maxChars.
function splitLabel(text, maxChars = 45) {
  if (!text || text.length <= maxChars) return [text];
  const mid = Math.floor(text.length / 2);
  for (let d = 0; d < mid; d++) {
    if (text[mid + d] === ' ')
      return [text.slice(0, mid + d).trim(), text.slice(mid + d).trim()];
    if (text[mid - d] === ' ')
      return [text.slice(0, mid - d).trim(), text.slice(mid - d).trim()];
  }
  return [text];
}

// Generate (before, after) rating pairs from pre/post bucket distributions.
// Rank-preserving pairing within each plan. Returns Map of "b·a" → count.
function generatePairsFromPlans(plans) {
  const allPairs = new Map();
  plans.forEach((plan) => {
    if (!plan.preDistribution) return;
    const pre = [];
    const post = [];
    plan.preDistribution.forEach((c, i) => {
      for (let k = 0; k < c; k++) pre.push(i + 1);
    });
    plan.distribution.forEach((c, i) => {
      for (let k = 0; k < c; k++) post.push(i + 1);
    });
    pre.sort((a, b) => a - b);
    post.sort((a, b) => a - b);
    const n = Math.min(pre.length, post.length);
    for (let i = 0; i < n; i++) {
      const key = `${pre[i]}·${post[i]}`;
      allPairs.set(key, (allPairs.get(key) || 0) + 1);
    }
  });
  return allPairs;
}

// Fixed Y-axis grid for the individual rate-of-change chart.
// All pairs (b·a) where after >= before, ordered bottom-to-top.
const ALL_PAIRS = (() => {
  const rows = [];
  for (let b = 1; b <= 10; b++) {
    for (let a = b; a <= 10; a++) {
      rows.push({ b, a, diagonal: a === b });
    }
  }
  return rows; // 55 entries
})();

// Aggregate per-method plans into a single distribution for display.
// Only plans whose method is in the selectedMethods Set are included.
function aggregatePlans(activities, selectedMethods) {
  return (activities || []).flatMap((a) => {
    const plans = (a.methods || []).filter((p) =>
      selectedMethods.has(p.method)
    );
    if (plans.length === 0) return [];
    const distribution = Array(10).fill(0);
    const hasPrePlans = plans.some((p) => p.preDistribution);
    const preDistribution = hasPrePlans ? Array(10).fill(0) : null;
    let respondents = 0;
    plans.forEach((p) => {
      p.distribution.forEach((v, i) => {
        distribution[i] += v;
      });
      respondents += p.respondents;
      if (preDistribution && p.preDistribution) {
        p.preDistribution.forEach((v, i) => {
          preDistribution[i] += v;
        });
      }
    });
    const count = distribution.reduce((s, v) => s + v, 0);
    const total = distribution.reduce((s, v, i) => s + v * (i + 1), 0);
    const participantsEvaluated =
      plans.reduce((s, p) => s + (p.participants || 0), 0) || respondents;
    return [
      {
        id: a.id,
        name: a.name,
        statusName: a.statusName,
        distribution,
        preDistribution,
        respondents: count > 0 ? count : respondents,
        participantsEvaluated,
        avgRating: count > 0 ? Math.round((total / count) * 10) / 10 : null,
        plans, // keep original plans for pair generation
      },
    ];
  });
}

// Build a plain-language prose summary.
function buildProseSummary(
  t,
  activities,
  outcomeName,
  methodRespondents,
  isPrePost,
  allMethods,
  hasThirdParty
) {
  if (!activities || activities.length === 0) return null;

  const n = activities.length;
  const totalRespondents = activities.reduce((s, a) => s + a.respondents, 0);
  if (totalRespondents === 0) return null;

  const agg = Array(10).fill(0);
  activities.forEach((a) =>
    a.distribution.forEach((v, i) => {
      agg[i] += v;
    })
  );

  const weightedSum = agg.reduce((s, v, i) => s + v * (i + 1), 0);
  const avg = Math.round((weightedSum / totalRespondents) * 10) / 10;

  const firstNZ = agg.findIndex((v) => v > 0);
  const lastNZ = 9 - [...agg].reverse().findIndex((v) => v > 0);
  const lowestRating = firstNZ + 1;
  const highestRating = lastNZ + 1;
  const spread = highestRating - lowestRating;

  const highCount = agg.slice(6).reduce((a, b) => a + b, 0);
  const lowCount = agg.slice(0, 5).reduce((a, b) => a + b, 0);
  const highPct = Math.round((highCount / totalRespondents) * 100);
  const lowPct = Math.round((lowCount / totalRespondents) * 100);

  const avgZone =
    avg >= 7
      ? t('charts.data_zones.upper_third')
      : avg >= 4
        ? t('charts.data_zones.middle_range')
        : t('charts.data_zones.lower_third');

  const parts = [];
  const smallSample = totalRespondents <= 6;

  // Respondent and evaluation-plan counts already appear on the stats line
  // above the chart (see StatsLine) — restating them here was pure noise.
  // The one thing not already on the stats line is how many activities the
  // responses are spread across, and only when that's genuinely informative
  // (2+) — "across one activity" was trivially obvious in a single-activity
  // view.
  if (n > 1) {
    parts.push(`Responses come from ${fmtN(n)} activities.`);
  }
  if (smallSample) {
    parts.push('The sample is small and results are indicative only.');
  }

  const distWord = hasThirdParty
    ? t('charts.assessments')
    : t('report.responses');
  const distWordLower = distWord.toLowerCase();

  if (spread <= 2) {
    parts.push(
      `${distWord} are tightly grouped between ${lowestRating} and ${highestRating}, suggesting a consistent result across participants.`
    );
  } else if (lowestRating >= 7) {
    parts.push(
      `All ${distWordLower} fall in the upper range of the scale, from ${lowestRating} to ${highestRating}.`
    );
  } else if (highPct >= 75) {
    parts.push(`${highPct}% of ${distWordLower} are in the 7–10 range.`);
  } else if (lowPct >= 75) {
    parts.push(
      `${lowPct}% of ${distWordLower} are in the lower half of the scale.`
    );
  } else {
    parts.push(`${distWord} range from ${lowestRating} to ${highestRating}.`);
  }

  const averageSelfEvident = spread <= 2 || isBellShaped(agg);
  if (!isPrePost && totalRespondents >= 5 && !averageSelfEvident) {
    parts.push(`The average rating of ${avg} sits in the ${avgZone}.`);
  }

  if (isPrePost) {
    const preAgg = Array(10).fill(0);
    let preTotal = 0;
    activities.forEach((a) => {
      if (a.preDistribution) {
        a.preDistribution.forEach((v, i) => {
          preAgg[i] += v;
        });
        preTotal += a.preDistribution.reduce((s, v) => s + v, 0);
      }
    });
    if (preTotal > 0) {
      const preAvgRaw =
        preAgg.reduce((s, v, i) => s + v * (i + 1), 0) / preTotal;
      const preAvg = Math.round(preAvgRaw * 10) / 10;
      const delta = avg - preAvg;
      const absDelta = Math.round(Math.abs(delta) * 10) / 10;
      const pctChange = Math.round((Math.abs(delta) / preAvg) * 100);
      const direction = delta >= 0 ? 'increase' : 'decrease';
      const magnitude =
        absDelta < 1 ? 'small' : absDelta <= 2 ? 'significant' : 'large';
      parts.push(
        `The average post rating of ${avg} compares to a pre rating of ${preAvg}, a ${magnitude} ${direction} of ${pctChange}%.`
      );
    } else if (totalRespondents >= 5 && !averageSelfEvident) {
      parts.push(`The average post rating of ${avg} sits in the ${avgZone}.`);
    }
  }

  if (methodRespondents.size > 0) {
    let selfCount = 0;
    methodRespondents.forEach((count, method) => {
      if (SELF_ASSESSED_RE.test(method)) selfCount += count;
    });
    const selfPct = Math.round((selfCount / totalRespondents) * 100);
    if (selfPct >= 25) {
      const hasNonManagerMethod = allMethods.some(
        (m) => !SELF_ASSESSED_RE.test(m)
      );
      const selfLabel = hasNonManagerMethod
        ? t('charts.nearly_100_pct')
        : t('charts.100_pct');
      parts.push(
        `${selfLabel} of these responses were Manager assessed.` +
          ` In this method the activity manager observes participants and estimates the rating` +
          ` they would give, then models a distribution using a standard deviation.` +
          ` This is a proxy measure, not a direct rating collected from participants.` +
          ` The distribution above may reflect the manager's judgement rather than` +
          ` participants' own experience, and should be interpreted with that in mind.`
      );
    }
  }

  return parts;
}

// Renders a vertical average line with rotated label.
function AvgLine({ x, top, bottom, label, color, fs }) {
  const labelFS = Math.round(fs * 0.85);
  const labelHalf = Math.round((label.length * labelFS * 0.58) / 2);
  const labelCY = top + labelHalf + 5;
  return (
    <g>
      <line x1={x} y1={top} x2={x} y2={bottom} stroke={color} strokeWidth={2} />
      <g transform={`translate(${x + 9}, ${labelCY})`}>
        <rect
          x={-(labelFS / 2 + 2)}
          y={-(labelHalf + 3)}
          width={labelFS + 4}
          height={labelHalf * 2 + 6}
          fill="rgba(255,255,255,0.5)"
          rx={2}
        />
        <text
          transform="rotate(-90)"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={labelFS}
          fontWeight={600}
          fill={color}
          style={{ userSelect: 'none' }}
        >
          {label}
        </text>
      </g>
    </g>
  );
}

// ── StatsLine — compact noun-phrase stats display ─────────────────────────────

function StatsLine({ plans, participants, respondents }) {
  const { t } = useTranslation();
  const rate =
    participants > 0 ? Math.round((respondents / participants) * 100) : null;
  const parts = [
    { v: plans, l: plans === 1 ? 'evaluation' : 'evaluations' },
    { v: participants, l: 'participants' },
    { v: respondents, l: 'responses' },
  ];

  return (
    <span className="or__stats-line">
      {parts.map((p, i) => (
        <Fragment key={p.l}>
          {i > 0 && ', '}
          <strong>{p.v.toLocaleString()}</strong> {p.l}
        </Fragment>
      ))}
      {rate !== null && (
        <Trans
          i18nKey="charts.outcome_ratings.pct_response_rate_fmt"
          values={{ rate }}
        />
      )}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const OutcomeRatings = forwardRef(function OutcomeRatings(
  {
    outcomeId,
    outcomeName: outcomePropName,
    activityIds = [],
    programIds = [],
    orgId = null,
    filterParams = {},
    onClose = null,
    initialIncluded = true,
  },
  ref
) {
  const { t } = useTranslation();
  useImperativeHandle(ref, () => ({ expand: () => setCollapsed(false) }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartMode, setChartMode] = useState('trend'); // 'trend' | 'individual'
  const [selectedMethods, setSelectedMethods] = useState(new Set());
  const [selectedActivities, setSelectedActivities] = useState(new Set());
  const [listOpen, setListOpen] = useState(false);
  const [svgWidth, setSvgWidth] = useState(
    Math.min(CHART_MAX_PX, Math.max(300, window.innerWidth * 0.96))
  );
  const [embedView, setEmbedView] = useState(false); // report preview mode
  const [included, setIncluded] = useState(initialIncluded); // "Show in report" toggle
  const [collapsed, setCollapsed] = useState(true); // panels start collapsed
  const [savingPng, setSavingPng] = useState(false);

  const svgRef = useRef(null);
  const captureRef = useRef(null); // wraps chart content for PNG export

  // Fetch data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    const params = { outcomeId };
    if (activityIds.length > 0) {
      params.activityIds = activityIds.join(',');
    } else if (programIds.length > 0) {
      params.programIds = programIds.join(',');
    } else if (orgId) {
      params.orgId = orgId;
    }
    Object.assign(params, filterParams);

    reportBuilderGet('charts/outcome-ratings', params)
      .then((j) => {
        if (cancelled) return;
        if (!j?.ok)
          throw new Error(j?.error || t('charts.errors.error_loading_ratings'));
        setData(j);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    outcomeId,
    activityIds.join(','),
    programIds.join(','),
    orgId,
    JSON.stringify(filterParams),
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialise all methods and activities as selected when data arrives
  useEffect(() => {
    if (data?.methods) setSelectedMethods(new Set(data.methods));
    if (data?.activities)
      setSelectedActivities(new Set(data.activities.map((a) => a.id)));
  }, [data]);

  // Measure container width via ResizeObserver
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setSvgWidth(Math.min(CHART_MAX_PX, w));
    };
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    measure();
    window.addEventListener('resize', measure);
    return () => {
      obs.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Methods actually relevant to the currently-selected activities.
  // Falls back to the full pool when nothing is selected so the method
  // filter and activity list stay visible and "all" reset remains functional.
  const relevantMethods = useMemo(() => {
    if (!data?.activities) return new Set();
    const pool =
      selectedActivities.size === 0
        ? data.activities
        : data.activities.filter((a) => selectedActivities.has(a.id));
    const methods = new Set();
    pool.forEach((a) => {
      (a.methods || []).forEach((p) => {
        if (p.method) methods.add(p.method);
      });
    });
    return methods;
  }, [data, selectedActivities]);

  // Keep selectedMethods in sync with relevantMethods — but only prune methods
  // that are no longer relevant, and only auto-add methods that are genuinely
  // new (weren't relevant before). Toggling an activity checkbox recomputes
  // relevantMethods on every change; without the "genuinely new" check, that
  // would re-add any method the user had deliberately unchecked via the
  // method filter, as long as some other still-selected activity uses it —
  // silently undoing the user's own method-filter choices.
  const prevRelevantMethodsRef = useRef(new Set());
  useEffect(() => {
    const prevRelevant = prevRelevantMethodsRef.current;
    setSelectedMethods((prev) => {
      const next = new Set(prev);
      prev.forEach((m) => {
        if (!relevantMethods.has(m)) next.delete(m);
      });
      relevantMethods.forEach((m) => {
        if (!prevRelevant.has(m) && !next.has(m)) next.add(m);
      });
      return next;
    });
    prevRelevantMethodsRef.current = relevantMethods;
  }, [relevantMethods]);

  // Aggregated activities respecting method filter
  const activities = useMemo(
    () => aggregatePlans(data?.activities, selectedMethods),
    [data, selectedMethods]
  );

  // Chart activities — method-filtered AND activity-checkbox-filtered
  const chartActivities = useMemo(
    () => activities.filter((a) => selectedActivities.has(a.id)),
    [activities, selectedActivities]
  );

  // ── Totals ──
  const totalRespondents = chartActivities.reduce(
    (s, a) => s + a.respondents,
    0
  );
  const totalParticipants = chartActivities.reduce(
    (s, a) => s + (a.participantsEvaluated || a.respondents),
    0
  );
  const totalPlans = useMemo(() => {
    const planIds = new Set();
    (data?.activities || [])
      .filter((a) => selectedActivities.has(a.id))
      .forEach((a) => {
        (a.methods || [])
          .filter((p) => selectedMethods.has(p.method))
          .forEach((p) => {
            (p.evaluationPlans || []).forEach((ep) => planIds.add(ep.id));
          });
      });
    return planIds.size;
  }, [data, selectedMethods, selectedActivities]);

  // Respondent counts per method — respects both activity and method filters
  const methodRespondents = useMemo(() => {
    const map = new Map();
    (data?.activities || []).forEach((a) => {
      if (!selectedActivities.has(a.id)) return;
      (a.methods || []).forEach((p) => {
        if (selectedMethods.has(p.method)) {
          map.set(p.method, (map.get(p.method) || 0) + p.respondents);
        }
      });
    });
    return map;
  }, [data, selectedMethods, selectedActivities]);

  const hasThirdParty = [...selectedMethods].some((m) =>
    THIRD_PARTY_RE.test(m)
  );

  // ── SVG layout ──
  // Labels float inside the chart area (no width reduction).
  const showActivityLabels =
    !data?.isPrePost &&
    chartMode === 'trend' &&
    chartActivities.length >= 2 &&
    chartActivities.length <= 10;

  const W = svgWidth;
  const SVG_HEIGHT = Math.max(200, Math.round(W / PHI));
  const chartW = W - PAD_LEFT - PAD_RIGHT;
  const chartH = SVG_HEIGHT - PAD_TOP - PAD_BOTTOM;
  const FS = Math.max(
    11,
    Math.min(16, Math.round(11 + ((chartW - 600) * 5) / 500))
  );

  const maxY =
    chartActivities.length > 0
      ? Math.max(1, ...chartActivities.flatMap((a) => a.distribution))
      : 1;
  const yMax = Math.ceil(maxY / 5) * 5 || 5;
  const yTicks = Array.from({ length: 6 }, (_, i) =>
    Math.round((yMax / 5) * i)
  );
  const fmtTick = (v) => (v >= 1000 ? (v / 1000).toFixed(1) + 'k' : String(v));

  const xScale = (i) => PAD_LEFT + (i / 9) * chartW;
  const yScale = (n) => (n / yMax) * chartH;
  const colW = chartW / 10;

  const outcomeName =
    data?.outcomeName ||
    outcomePropName ||
    `${t('common.outcome')} ${outcomeId}`;
  // X-axis annotation reads as "<label> ←→" (how much of the measure was
  // reported), not a sentence — so it needs a noun phrase, not the graded
  // sentence text (e.g. "Social connectedness improved" doesn't fit that
  // grammar). sOutcome.shortLabel is exactly that noun phrase; the old
  // displayOutcome() hardcoded lookup only covered 13 of 22 outcomes and
  // wasn't DB-driven, so it's now just the fallback for any outcome that
  // somehow lacks a shortLabel.
  const displayName =
    data?.outcomeShortLabel ||
    displayOutcome(outcomeName, data?.outcomeId || outcomeId);

  // Prose
  const prose = buildProseSummary(
    t,
    chartActivities,
    outcomeName,
    methodRespondents,
    data?.isPrePost ?? false,
    data?.methods || [],
    hasThirdParty
  );

  const toggleMethod = useCallback((m) => {
    setSelectedMethods((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
  }, []);

  // ── Save report view as PNG ──
  const handleSavePng = useCallback(async () => {
    if (!captureRef.current) return;
    setSavingPng(true);
    try {
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      const slug = outcomeName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      link.download = `${slug}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setSavingPng(false);
    }
  }, [outcomeName]);

  // ── Render ──
  return (
    <div className="or">
      {/* ── Control bar ────────────────────────────────────────────────────── */}
      <div
        className={`or__controls${collapsed ? ' or__controls--collapsed' : ''}`}
      >
        {/* Measure label — LHS, clickable to collapse/expand */}
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="or__label-btn"
        >
          <span className="or__label-btn-caret">{collapsed ? '▸' : '▾'}</span>
          <span className="or__label-btn-name">{outcomeName}</span>
        </button>

        {/* Show in report Yes/No toggle */}
        <span className="or__include-label">
          {t('activity.report.show_in_report')}
        </span>
        <div className="or__include-btns">
          {['Yes', 'No'].map((btn, i) => {
            const active = btn === 'Yes' ? included : !included;
            return (
              <button
                key={btn}
                onClick={() => {
                  if (!active) setIncluded(btn === 'Yes');
                }}
                className={`or__include-btn${
                  i === 0 ? ' or__include-btn--sep' : ''
                }${active ? ' or__include-btn--active' : ''}`}
              >
                {btn === 'Yes' ? t('actions.yes') : t('actions.no')}
              </button>
            );
          })}
        </div>

        {/* Report view toggle */}
        <button
          onClick={() => {
            setEmbedView((v) => !v);
            if (collapsed) setCollapsed(false);
          }}
          className={`or__report-view-btn${
            embedView ? ' or__report-view-btn--on' : ''
          }`}
        >
          <span className="or__report-view-icon">{embedView ? '⊠' : '⊡'}</span>
          {embedView ? t('report.exit_report_view') : t('report.report_view')}
        </button>

        {/* Close */}
        {onClose && (
          <button
            onClick={onClose}
            title={t('reports.remove_this_chart')}
            className="or__close-btn"
          >
            ✕
          </button>
        )}
      </div>

      {/* ── Collapsible body ─────────────────────────────────────────────────── */}
      {!collapsed && (
        <>
          {/* Report-view indicator bar */}
          {embedView && (
            <div className="or__embed-bar">
              <span>{t('charts.outcome_ratings.report_view_title')}</span>
              <button
                className="or__embed-save-btn"
                onClick={handleSavePng}
                disabled={savingPng}
              >
                {savingPng
                  ? `${t('common.saving')}…`
                  : `⬇ ${t('common.save_png')}`}
              </button>
            </div>
          )}

          {/* Capture area — everything below the embed bar */}
          <div ref={captureRef}>
            {/* Heading */}
            <div className="or__heading-wrap">
              <h3 className="or__heading">
                <span className="or__heading-prefix">
                  {embedView
                    ? `${t('report.outcome_measure')}:`
                    : `${t('charts.outcome_ratings.measure')}:`}
                </span>
                <span>{outcomeName}</span>
              </h3>
            </div>

            {/* Stats row: plans · participants · responses */}
            {!loading && !error && chartActivities.length > 0 && (
              <div className="or__stats">
                <StatsLine
                  plans={totalPlans}
                  participants={totalParticipants}
                  respondents={totalRespondents}
                />
              </div>
            )}

            {/* Method filter — hidden in report view */}
            {!embedView && !loading && !error && relevantMethods.size > 0 && (
              <div className="or__method-bar">
                <span className="or__method-label">{t('charts.method')}</span>
                {(data?.methods || [])
                  .filter((m) => relevantMethods.has(m))
                  .map((m) => {
                    if (relevantMethods.size === 1) {
                      return (
                        <span key={m} className="or__method-single">
                          {m}
                        </span>
                      );
                    }
                    const checked = selectedMethods.has(m);
                    return (
                      <label key={m} className="or__method-check">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleMethod(m)}
                          style={{
                            cursor: 'pointer',
                            accentColor: `rgb(${BASE_COLOR})`,
                          }}
                        />
                        {m}
                      </label>
                    );
                  })}
              </div>
            )}

            {/* Loading / error / empty states */}
            {loading && <div className="or__state">{t('common.loading')}…</div>}
            {!loading && error && (
              <div className="or__state or__state--error">{error}</div>
            )}
            {!loading && !error && chartActivities.length === 0 && (
              <div className="or__state">
                {selectedMethods.size === 0
                  ? t('charts.outcome_ratings.select_an_eval_method')
                  : selectedActivities.size === 0
                    ? t('charts.outcome_ratings.select_an_activity')
                    : t(
                        'charts.outcome_ratings.no_evaluation_data_for_this_outcome_under_current_filter'
                      )}
              </div>
            )}

            {!loading && !error && activities.length > 0 && (
              <>
                {/* ── Chart + prose — only when at least one activity is selected ── */}
                {chartActivities.length > 0 && (
                  <>
                    {/* Chart mode tabs — prePost always; post-only when 2+ activities */}
                    {!embedView &&
                      (data?.isPrePost || chartActivities.length > 1) && (
                        <div className="or__chart-tabs">
                          {[
                            {
                              mode: 'trend',
                              label: data?.isPrePost
                                ? t('charts.modes.trend_rate_of_change')
                                : t('charts.modes.trend_separate'),
                            },
                            ...(data?.isPrePost
                              ? [
                                  {
                                    mode: 'individual',
                                    label: t(
                                      'charts.modes.individual_rate_of_change'
                                    ),
                                  },
                                ]
                              : []),
                            ...(!data?.isPrePost && chartActivities.length > 1
                              ? [
                                  {
                                    mode: 'aggregate',
                                    label: t('chart_types.aggregate'),
                                  },
                                ]
                              : []),
                          ].map(({ mode, label }) => {
                            const active = chartMode === mode;
                            return (
                              <button
                                key={mode}
                                onClick={() => setChartMode(mode)}
                                className={`or__chart-tab${
                                  active ? ' or__chart-tab--active' : ''
                                }`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}

                    {/* SVG measurement div */}
                    <div ref={svgRef} className="or__svg-wrap">
                      <svg
                        viewBox={`0 0 ${W} ${SVG_HEIGHT}`}
                        width={W}
                        height={SVG_HEIGHT}
                        preserveAspectRatio="xMidYMid meet"
                        className="or__svg"
                      >
                        <defs>
                          <marker
                            id="or-arrowHead"
                            markerWidth="7"
                            markerHeight="7"
                            refX="6"
                            refY="3.5"
                            orient="auto"
                          >
                            <path d="M 0 0 L 7 3.5 L 0 7 Z" fill={AXIS_COLOR} />
                          </marker>
                        </defs>

                        {/* Before/After legend — prePost only */}
                        {data?.isPrePost &&
                          chartActivities.length > 0 &&
                          (() => {
                            const ly = Math.round(PAD_TOP / 2);
                            const sh = 12,
                              sw = 24,
                              fs = 12;
                            const lx = PAD_LEFT + 20;
                            return (
                              <g>
                                <g transform={`translate(${lx}, ${ly})`}>
                                  <rect
                                    x={0}
                                    y={-sh / 2}
                                    width={sw}
                                    height={sh}
                                    rx={2}
                                    fill={`rgba(${PRE_COLOR},0.6)`}
                                  />
                                  <text
                                    x={sw + 4}
                                    y={0}
                                    dominantBaseline="middle"
                                    fontSize={fs}
                                    fill="#6b7280"
                                  >
                                    {t('charts.before')}
                                  </text>
                                </g>
                                <g transform={`translate(${lx + 90}, ${ly})`}>
                                  <rect
                                    x={0}
                                    y={-sh / 2}
                                    width={sw}
                                    height={sh}
                                    rx={2}
                                    fill={`rgba(${BASE_COLOR},0.6)`}
                                  />
                                  <text
                                    x={sw + 4}
                                    y={0}
                                    dominantBaseline="middle"
                                    fontSize={fs}
                                    fill="#6b7280"
                                  >
                                    {t('charts.after')}
                                  </text>
                                </g>
                              </g>
                            );
                          })()}

                        {/* Y grid lines — trend chart only */}
                        {chartMode === 'trend' &&
                          yTicks.map((v) => {
                            const y = PAD_TOP + chartH - yScale(v);
                            return (
                              <g key={v}>
                                <line
                                  x1={PAD_LEFT}
                                  y1={y}
                                  x2={PAD_LEFT + chartW}
                                  y2={y}
                                  stroke="#f3f4f6"
                                  strokeWidth={1}
                                />
                                <text
                                  x={PAD_LEFT - 6}
                                  y={y}
                                  textAnchor="end"
                                  dominantBaseline="middle"
                                  fontSize={Math.round(FS * 0.85)}
                                  fill={AXIS_COLOR}
                                >
                                  {fmtTick(v)}
                                </text>
                              </g>
                            );
                          })}

                        {/* X axis baseline */}
                        <line
                          x1={PAD_LEFT}
                          y1={PAD_TOP + chartH}
                          x2={PAD_LEFT + chartW}
                          y2={PAD_TOP + chartH}
                          stroke="#e5e7eb"
                          strokeWidth={1}
                        />

                        {/* ── Individual rate-of-change chart ── */}
                        {chartMode === 'individual' &&
                          (() => {
                            // Build pairs using fallback rank-pairing from bucket distributions
                            const dataPairs = new Map();
                            (data?.activities || []).forEach((a) => {
                              const activePlans = (a.methods || []).filter(
                                (p) =>
                                  selectedMethods.has(p.method) &&
                                  p.preDistribution &&
                                  p.preDistribution.some((v) => v > 0)
                              );
                              generatePairsFromPlans(activePlans).forEach(
                                (cnt, key) =>
                                  dataPairs.set(
                                    key,
                                    (dataPairs.get(key) || 0) + cnt
                                  )
                              );
                            });

                            const nRows = ALL_PAIRS.length;
                            const rowH = chartH / nRows;
                            const barH = Math.max(3, Math.min(14, rowH * 0.68));
                            const labelFS = Math.max(8, Math.round(FS * 0.72));

                            const totalResponses = Array.from(
                              dataPairs.values()
                            ).reduce((s, v) => s + v, 0);
                            const perLayer = areaOpacityFor(
                              Math.max(1, totalResponses)
                            );
                            const barOpacity = (cnt) =>
                              Math.min(0.92, 1 - Math.pow(1 - perLayer, cnt));

                            return (
                              <>
                                {/* Alternating column backgrounds */}
                                {RATINGS.map(
                                  (r, i) =>
                                    i % 2 === 1 && (
                                      <rect
                                        key={r}
                                        x={PAD_LEFT + i * colW}
                                        y={PAD_TOP}
                                        width={colW}
                                        height={chartH}
                                        fill="#f9fafb"
                                      />
                                    )
                                )}
                                {/* Column boundary grid lines */}
                                {Array.from({ length: 11 }, (_, i) => (
                                  <line
                                    key={i}
                                    x1={PAD_LEFT + i * colW}
                                    y1={PAD_TOP}
                                    x2={PAD_LEFT + i * colW}
                                    y2={PAD_TOP + chartH}
                                    stroke="#e5e7eb"
                                    strokeWidth={1}
                                  />
                                ))}

                                {ALL_PAIRS.map(({ b, a, diagonal }, idx) => {
                                  const rowY =
                                    PAD_TOP + chartH - (idx + 1) * rowH;
                                  const barY = rowY + (rowH - barH) / 2;
                                  const key = `${b}·${a}`;
                                  const cnt = dataPairs.get(key) || 0;
                                  const bX = PAD_LEFT + (b - 1) * colW;
                                  const bW = (a - b + 1) * colW;
                                  const op = cnt > 0 ? barOpacity(cnt) : 0;

                                  return (
                                    <g key={key}>
                                      {diagonal && (
                                        <line
                                          x1={PAD_LEFT}
                                          y1={rowY + rowH / 2}
                                          x2={PAD_LEFT + chartW}
                                          y2={rowY + rowH / 2}
                                          stroke="#e5e7eb"
                                          strokeWidth={0.5}
                                          strokeDasharray="2,4"
                                        />
                                      )}
                                      {cnt > 0 && (
                                        <rect
                                          x={bX}
                                          y={barY}
                                          width={bW}
                                          height={barH}
                                          fill={`rgb(${BASE_COLOR})`}
                                          rx={1}
                                          opacity={op}
                                        />
                                      )}
                                      {diagonal && (
                                        <text
                                          x={PAD_LEFT - 6}
                                          y={rowY + rowH / 2}
                                          textAnchor="end"
                                          dominantBaseline="middle"
                                          fontSize={labelFS}
                                          fill={
                                            cnt > 0 ? LABEL_COLOR : AXIS_COLOR
                                          }
                                          fontWeight={cnt > 0 ? 600 : 400}
                                        >
                                          {b}·{a}
                                        </text>
                                      )}
                                    </g>
                                  );
                                })}

                                {/* X axis ticks centred in each column */}
                                {RATINGS.map((r, i) => (
                                  <g key={r}>
                                    <line
                                      x1={PAD_LEFT + (i + 0.5) * colW}
                                      y1={PAD_TOP + chartH}
                                      x2={PAD_LEFT + (i + 0.5) * colW}
                                      y2={PAD_TOP + chartH + 5}
                                      stroke={AXIS_COLOR}
                                      strokeWidth={1}
                                    />
                                    <text
                                      x={PAD_LEFT + (i + 0.5) * colW}
                                      y={PAD_TOP + chartH + 16}
                                      textAnchor="middle"
                                      fontSize={FS}
                                      fill={LABEL_COLOR}
                                    >
                                      {r}
                                    </text>
                                  </g>
                                ))}
                              </>
                            );
                          })()}

                        {/* ── Aggregate bar chart (post-only) ── */}
                        {chartMode === 'aggregate' &&
                          (() => {
                            const aggDist = Array(10).fill(0);
                            chartActivities.forEach((a) =>
                              a.distribution.forEach((v, i) => {
                                aggDist[i] += v;
                              })
                            );
                            const aggTotal = aggDist.reduce((s, v) => s + v, 0);
                            const aggMax = Math.max(...aggDist, 1);
                            const aggYMax = Math.ceil(aggMax / 5) * 5 || 5;
                            const aggYTicks = Array.from(
                              { length: 6 },
                              (_, i) => Math.round((aggYMax / 5) * i)
                            );
                            const aggYScale = (n) => (n / aggYMax) * chartH;
                            const aggAvg =
                              aggTotal > 0
                                ? aggDist.reduce(
                                    (s, v, i) => s + v * (i + 1),
                                    0
                                  ) / aggTotal
                                : null;

                            return (
                              <>
                                {/* Y grid lines */}
                                {aggYTicks.map((v) => {
                                  const y = PAD_TOP + chartH - aggYScale(v);
                                  return (
                                    <g key={v}>
                                      <line
                                        x1={PAD_LEFT}
                                        y1={y}
                                        x2={PAD_LEFT + chartW}
                                        y2={y}
                                        stroke="#f3f4f6"
                                        strokeWidth={1}
                                      />
                                      <text
                                        x={PAD_LEFT - 6}
                                        y={y}
                                        textAnchor="end"
                                        dominantBaseline="middle"
                                        fontSize={Math.round(FS * 0.85)}
                                        fill={AXIS_COLOR}
                                      >
                                        {fmtTick(v)}
                                      </text>
                                    </g>
                                  );
                                })}

                                {/* Single aggregated area — same style as trend areas */}
                                <path
                                  d={buildPath(
                                    aggDist,
                                    xScale,
                                    aggYScale,
                                    chartH,
                                    PAD_TOP
                                  )}
                                  fill={`rgba(${BASE_COLOR},0.55)`}
                                  stroke={`rgba(${BASE_COLOR},0.9)`}
                                  strokeWidth={1}
                                />

                                {/* Average line */}
                                {aggAvg != null && (
                                  <AvgLine
                                    x={xScale(aggAvg - 1)}
                                    top={PAD_TOP}
                                    bottom={PAD_TOP + chartH}
                                    label={t('charts.average')}
                                    color={`rgb(${BASE_COLOR})`}
                                    fs={FS}
                                  />
                                )}
                              </>
                            );
                          })()}

                        {/* ── Trend area chart ── */}
                        {chartMode === 'trend' &&
                          (() => {
                            const areaOpacity = areaOpacityFor(
                              chartActivities.length
                            );
                            const colorFor = (a) =>
                              isQuickResponseOnly(a)
                                ? QUICK_RESPONSE_COLOR
                                : BASE_COLOR;
                            const preColorFor = (a) =>
                              isQuickResponseOnly(a)
                                ? QUICK_RESPONSE_COLOR
                                : PRE_COLOR;
                            const fillFor = (color) =>
                              `rgba(${color},${areaOpacity})`;
                            const strokeFor = (color) =>
                              `rgba(${color},${Math.min(1, areaOpacity * 2)})`;

                            const hasPreData =
                              data?.isPrePost &&
                              chartActivities.some(
                                (a) =>
                                  a.preDistribution &&
                                  a.preDistribution.some((v) => v > 0)
                              );

                            return (
                              <>
                                {/* Post areas */}
                                {chartActivities.map((a) => (
                                  <path
                                    key={`post-${a.id}`}
                                    d={buildPath(
                                      a.distribution,
                                      xScale,
                                      yScale,
                                      chartH,
                                      PAD_TOP
                                    )}
                                    fill={fillFor(colorFor(a))}
                                    stroke={strokeFor(colorFor(a))}
                                    strokeWidth={1}
                                  />
                                ))}
                                {/* Pre areas */}
                                {hasPreData &&
                                  chartActivities.map((a) => {
                                    if (
                                      !a.preDistribution ||
                                      !a.preDistribution.some((v) => v > 0)
                                    )
                                      return null;
                                    return (
                                      <path
                                        key={`pre-${a.id}`}
                                        d={buildPath(
                                          a.preDistribution,
                                          xScale,
                                          yScale,
                                          chartH,
                                          PAD_TOP
                                        )}
                                        fill={fillFor(preColorFor(a))}
                                        stroke={strokeFor(preColorFor(a))}
                                        strokeWidth={1}
                                      />
                                    );
                                  })}

                                {/* Average lines */}
                                {(() => {
                                  const postAgg = Array(10).fill(0);
                                  chartActivities.forEach((a) =>
                                    a.distribution.forEach((v, i) => {
                                      postAgg[i] += v;
                                    })
                                  );
                                  const postTotal = postAgg.reduce(
                                    (s, v) => s + v,
                                    0
                                  );
                                  const postAvg =
                                    postTotal > 0
                                      ? postAgg.reduce(
                                          (s, v, i) => s + v * (i + 1),
                                          0
                                        ) / postTotal
                                      : null;

                                  let preAvg = null;
                                  if (hasPreData) {
                                    const preAgg = Array(10).fill(0);
                                    chartActivities.forEach((a) => {
                                      if (a.preDistribution)
                                        a.preDistribution.forEach((v, i) => {
                                          preAgg[i] += v;
                                        });
                                    });
                                    const preTotal = preAgg.reduce(
                                      (s, v) => s + v,
                                      0
                                    );
                                    if (preTotal > 0)
                                      preAvg =
                                        preAgg.reduce(
                                          (s, v, i) => s + v * (i + 1),
                                          0
                                        ) / preTotal;
                                  }

                                  return (
                                    <>
                                      {postAvg != null && (
                                        <AvgLine
                                          x={xScale(postAvg - 1)}
                                          top={PAD_TOP}
                                          bottom={PAD_TOP + chartH}
                                          label={
                                            data?.isPrePost
                                              ? t('charts.average_after')
                                              : t('charts.average')
                                          }
                                          color={`rgb(${BASE_COLOR})`}
                                          fs={FS}
                                        />
                                      )}
                                      {preAvg != null && (
                                        <AvgLine
                                          x={xScale(preAvg - 1)}
                                          top={PAD_TOP}
                                          bottom={PAD_TOP + chartH}
                                          label={t('charts.average_before')}
                                          color={`rgb(${PRE_COLOR})`}
                                          fs={FS}
                                        />
                                      )}
                                    </>
                                  );
                                })()}

                                {/* ── Activity labels: 30° geometry, closest-first search ── */}
                                {showActivityLabels &&
                                  (() => {
                                    // Geometry: for each candidate offset (label right edge px left of
                                    // peak), compute the label Y that produces a ~30° connector angle.
                                    // The connector has a 5 px horizontal elbow stub then goes diagonal
                                    // to the peak — never horizontal or vertical.
                                    //
                                    // The label Y is derived from the angle, NOT from the curve ceiling.
                                    // We then check that the 30°-computed Y is actually in whitespace
                                    // (above the composite curve ceiling at the label's X range).
                                    // The label can be above OR below the peak — whichever side puts
                                    // it in genuine whitespace with no overlap.
                                    //
                                    // Connector: (labelRightX, labelY) → stub 5px horizontal →
                                    //            diagonal to (peakX, peakY).

                                    const TAN30 = Math.tan(
                                      (30 * Math.PI) / 180
                                    ); // ≈ 0.577
                                    const STUB = 5; // horizontal stub at elbow (px)
                                    const MARGIN = 22; // clearance from label box edge to curve top (px)
                                    const extTop = 4; // labels may float above chart into SVG margin
                                    const labelFS = FS;
                                    // charWpx: conservative estimate so rect stays tight to text
                                    const charWpx = labelFS * 0.54;
                                    const MIN_SEP = Math.max(labelFS + 10, 24);
                                    const xStep = chartW / 9;
                                    const cBot = PAD_TOP + chartH - 6;

                                    // compCeil[i]: SVG Y of the tallest curve top at rating index i.
                                    // Smaller SVG Y = taller curve = less room above.
                                    const compCeil = RATINGS.map((_, idx) => {
                                      const maxCount = chartActivities.reduce(
                                        (m, a) =>
                                          Math.max(m, a.distribution[idx] || 0),
                                        0
                                      );
                                      return (
                                        PAD_TOP + chartH - yScale(maxCount)
                                      );
                                    });

                                    // Highest curve (smallest SVG Y) over a rating-index range.
                                    const ceilInRange = (from, to) => {
                                      if (from > to) return PAD_TOP + chartH;
                                      return Math.min(
                                        ...compCeil.slice(from, to + 1)
                                      );
                                    };

                                    // Average line X — same combined calculation as the chart renders.
                                    // Labels must stay clear of this vertical line and its text label.
                                    const postAgg = Array(10).fill(0);
                                    chartActivities.forEach((a) =>
                                      a.distribution.forEach((v, i) => {
                                        postAgg[i] += v;
                                      })
                                    );
                                    const postTotal = postAgg.reduce(
                                      (s, v) => s + v,
                                      0
                                    );
                                    const postAvgVal =
                                      postTotal > 0
                                        ? postAgg.reduce(
                                            (s, v, i) => s + v * (i + 1),
                                            0
                                          ) / postTotal
                                        : null;
                                    const avgLineX =
                                      postAvgVal != null
                                        ? xScale(postAvgVal - 1)
                                        : null;
                                    // Margin either side of the average line that labels must not enter.
                                    // Wide enough to also clear the rotated "Average" text label.
                                    const AVG_EXCL = 18;

                                    // Check if a label box (centred at labelY, right edge rX, left lX)
                                    // is genuinely in whitespace — above curves AND clear of avg line.
                                    const labelClears = (
                                      labelY,
                                      tW,
                                      lX,
                                      rX
                                    ) => {
                                      // 1. Within extended vertical bounds (extTop allows above-chart)
                                      if (labelY < extTop || labelY > cBot)
                                        return false;
                                      // 2. If label is above the chart data area: trivially clear of
                                      //    all curves and the average line — no further checks needed.
                                      if (labelY < PAD_TOP) return true;
                                      // 3. Label bottom must clear composite curve ceiling
                                      const fromIdx = Math.max(
                                        0,
                                        Math.floor((lX - PAD_LEFT) / xStep)
                                      );
                                      const toIdx = Math.max(
                                        fromIdx,
                                        Math.min(
                                          9,
                                          Math.ceil((rX - PAD_LEFT) / xStep)
                                        )
                                      );
                                      const minCeil = ceilInRange(
                                        fromIdx,
                                        toIdx
                                      );
                                      if (
                                        labelY + labelFS / 2 + MARGIN >
                                        minCeil
                                      )
                                        return false;
                                      // 4. Label X range must not intersect the average line ± AVG_EXCL
                                      if (
                                        avgLineX != null &&
                                        rX + AVG_EXCL > avgLineX &&
                                        lX - AVG_EXCL < avgLineX
                                      )
                                        return false;
                                      return true;
                                    };

                                    // Candidate horizontal offsets from peak (px), closest first.
                                    const OFFSETS = [
                                      20, 35, 55, 80, 115, 160, 215, 280,
                                    ];

                                    const labeled = chartActivities
                                      .map((a) => {
                                        const maxVal = Math.max(
                                          ...a.distribution,
                                          0
                                        );
                                        const peakIdx =
                                          maxVal > 0
                                            ? a.distribution.indexOf(maxVal)
                                            : 0;
                                        const peakX = xScale(peakIdx);
                                        const peakY =
                                          PAD_TOP + chartH - yScale(maxVal);
                                        const name = a.name || '';

                                        // Minimum offset before an above-chart label is allowed —
                                        // ensures the connector is long enough that the label lands
                                        // well to the left (near other above-chart labels).
                                        const aboveChartMinOffset =
                                          avgLineX != null
                                            ? Math.max(
                                                260,
                                                Math.ceil(
                                                  peakX -
                                                    avgLineX +
                                                    AVG_EXCL +
                                                    20
                                                )
                                              )
                                            : 260;

                                        let chosen = null; // first valid "above-peak" placement
                                        let belowPk = null; // first valid "below-peak" placement
                                        let bestFB = null; // fallback: most ceiling clearance

                                        for (const offset of OFFSETS) {
                                          const rX = peakX - offset;
                                          if (rX < PAD_LEFT + 24) break;

                                          // Text at this position
                                          const availW = Math.max(
                                            20,
                                            rX - PAD_LEFT - 4
                                          );
                                          const maxCh = Math.floor(
                                            availW / charWpx
                                          );
                                          const labelTxt =
                                            name.length > maxCh
                                              ? name.slice(0, maxCh - 1) + '…'
                                              : name;
                                          const tW =
                                            Math.min(name.length, maxCh) *
                                            charWpx;
                                          const lX = rX - tW;

                                          // Horizontal distance from elbow to peak determines the
                                          // vertical distance for a 30° angle.
                                          const dx = peakX - (rX + STUB); // elbow → peak
                                          if (dx <= 0) continue;
                                          const targetDy = dx * TAN30; // |dy| for 30°

                                          // Option A: label ABOVE peak (connector goes down-right to peak)
                                          const yAbove = peakY - targetDy;
                                          // Option B: label BELOW peak (connector goes up-right to peak)
                                          const yBelow = peakY + targetDy;

                                          if (
                                            !chosen &&
                                            labelClears(yAbove, tW, lX, rX)
                                          ) {
                                            // For above-chart placements, enforce minimum offset so labels
                                            // land to the left, grouped near other above-chart labels.
                                            if (
                                              yAbove < PAD_TOP &&
                                              offset < aboveChartMinOffset
                                            ) {
                                              // skip — too close; keep searching larger offsets
                                            } else {
                                              chosen = {
                                                rX,
                                                lX,
                                                tW,
                                                labelTxt,
                                                desiredY: Math.max(
                                                  extTop,
                                                  yAbove
                                                ),
                                              };
                                              break;
                                            }
                                          }
                                          if (
                                            !belowPk &&
                                            labelClears(yBelow, tW, lX, rX)
                                          ) {
                                            belowPk = {
                                              rX,
                                              lX,
                                              tW,
                                              labelTxt,
                                              desiredY: yBelow,
                                            };
                                            // don't break — still look for an "above" placement
                                          }

                                          // Fallback: ceiling-based Y, best clearance so far
                                          const fromIdx = Math.max(
                                            0,
                                            Math.floor((lX - PAD_LEFT) / xStep)
                                          );
                                          const toIdx = Math.max(
                                            fromIdx,
                                            Math.min(
                                              9,
                                              Math.ceil((rX - PAD_LEFT) / xStep)
                                            )
                                          );
                                          const minCeil = ceilInRange(
                                            fromIdx,
                                            toIdx
                                          );
                                          if (
                                            !bestFB ||
                                            minCeil > bestFB.minCeil
                                          ) {
                                            bestFB = {
                                              rX,
                                              lX,
                                              tW,
                                              labelTxt,
                                              minCeil,
                                              desiredY: Math.max(
                                                extTop,
                                                minCeil - MARGIN - labelFS / 2
                                              ),
                                            };
                                          }
                                        }

                                        const pick =
                                          chosen || belowPk || bestFB;
                                        if (!pick) return null;

                                        return {
                                          a,
                                          peakX,
                                          peakY,
                                          labelRightX: pick.rX,
                                          labelLeftX: pick.lX,
                                          textW: pick.tW,
                                          label: pick.labelTxt,
                                          desiredY: pick.desiredY,
                                        };
                                      })
                                      .filter(Boolean);

                                    if (labeled.length === 0) return null;

                                    // Non-crossing sort: order labels so their vertical sequence
                                    // matches the vertical sequence of their target peaks.
                                    // Rule: topmost label → highest-amplitude peak (smallest SVG Y).
                                    // When label order mirrors peak order, connector lines fan out
                                    // without crossing each other.
                                    // Tie-break by peak X ascending so that within the same
                                    // amplitude band, the leftmost peak gets the topmost label —
                                    // lines stay parallel rather than crisscrossing.
                                    labeled.sort((a, b) => {
                                      const dy = a.peakY - b.peakY;
                                      if (Math.abs(dy) > 4) return dy; // different amplitudes
                                      return a.peakX - b.peakX; // same band: left→top
                                    });

                                    // Assign Y top-to-bottom, using desiredY as a lower bound so
                                    // labels stay at their geometry-computed position when possible.
                                    // extTop (not cTop) allows above-chart labels to float upward.
                                    const ys = [];
                                    for (let i = 0; i < labeled.length; i++) {
                                      const floor =
                                        i === 0 ? extTop : ys[i - 1] + MIN_SEP;
                                      ys.push(
                                        Math.max(floor, labeled[i].desiredY)
                                      );
                                    }
                                    // Shift whole group up if the last label overflows the bottom
                                    if (ys[ys.length - 1] > cBot) {
                                      const shift = ys[ys.length - 1] - cBot;
                                      for (let i = 0; i < ys.length; i++)
                                        ys[i] -= shift;
                                    }
                                    for (let i = 0; i < ys.length; i++)
                                      ys[i] = Math.max(
                                        extTop,
                                        Math.min(cBot, ys[i])
                                      );

                                    // Post-spread ceiling correction: stacking may have pushed
                                    // in-chart labels down onto curve peaks. Re-check each label
                                    // that sits inside the chart area and lift it to safe Y if needed.
                                    for (let i = 0; i < ys.length; i++) {
                                      if (ys[i] < PAD_TOP) continue; // above chart — curves don't apply
                                      const {
                                        labelLeftX: lX,
                                        labelRightX: rX,
                                      } = labeled[i];
                                      const fromIdx = Math.max(
                                        0,
                                        Math.floor((lX - PAD_LEFT) / xStep)
                                      );
                                      const toIdx = Math.max(
                                        fromIdx,
                                        Math.min(
                                          9,
                                          Math.ceil((rX - PAD_LEFT) / xStep)
                                        )
                                      );
                                      const minCeil = ceilInRange(
                                        fromIdx,
                                        toIdx
                                      );
                                      if (
                                        ys[i] + labelFS / 2 + MARGIN >
                                        minCeil
                                      ) {
                                        ys[i] = Math.max(
                                          extTop,
                                          minCeil - MARGIN - labelFS / 2
                                        );
                                      }
                                    }

                                    // Re-enforce MIN_SEP bottom-to-top after ceiling correction.
                                    // Going upward (smaller SVG Y) never worsens curve clearance,
                                    // whereas pushing labels down would put them back onto curves.
                                    for (let i = ys.length - 2; i >= 0; i--) {
                                      if (ys[i + 1] - ys[i] < MIN_SEP)
                                        ys[i] = ys[i + 1] - MIN_SEP;
                                    }
                                    for (let i = 0; i < ys.length; i++)
                                      ys[i] = Math.max(
                                        extTop,
                                        Math.min(cBot, ys[i])
                                      );

                                    return labeled.map(
                                      (
                                        {
                                          a,
                                          peakX,
                                          peakY,
                                          labelRightX,
                                          labelLeftX,
                                          textW,
                                          label,
                                        },
                                        i
                                      ) => {
                                        const labelY = ys[i];
                                        const elbowX = labelRightX + STUB;
                                        return (
                                          <g key={a.id}>
                                            {/* 5px horizontal stub then diagonal to peak */}
                                            <polyline
                                              points={`${labelRightX},${labelY} ${elbowX},${labelY} ${peakX},${peakY}`}
                                              fill="none"
                                              stroke="#94a3b8"
                                              strokeWidth={1}
                                              strokeLinejoin="round"
                                            />
                                            <circle
                                              cx={peakX}
                                              cy={peakY}
                                              r={2.5}
                                              fill={`rgb(${BASE_COLOR})`}
                                              opacity={0.65}
                                            />
                                            {/* tight white background behind label text */}
                                            <rect
                                              x={labelLeftX - 1}
                                              y={labelY - labelFS / 2 - 1}
                                              width={textW + 2}
                                              height={labelFS + 2}
                                              fill="white"
                                              opacity={0.5}
                                              rx={1}
                                            />
                                            <text
                                              x={labelLeftX}
                                              y={labelY}
                                              fontSize={labelFS}
                                              fill={LABEL_COLOR}
                                              dominantBaseline="middle"
                                            >
                                              {label}
                                            </text>
                                          </g>
                                        );
                                      }
                                    );
                                  })()}
                              </>
                            );
                          })()}

                        {/* X axis ticks — trend and aggregate charts */}
                        {(chartMode === 'trend' || chartMode === 'aggregate') &&
                          RATINGS.map((r, i) => {
                            const x = xScale(i);
                            return (
                              <g key={r}>
                                <line
                                  x1={x}
                                  y1={PAD_TOP + chartH}
                                  x2={x}
                                  y2={PAD_TOP + chartH + 5}
                                  stroke={AXIS_COLOR}
                                  strokeWidth={1}
                                />
                                <text
                                  x={x}
                                  y={PAD_TOP + chartH + 16}
                                  textAnchor="middle"
                                  fontSize={FS}
                                  fill={LABEL_COLOR}
                                >
                                  {r}
                                </text>
                              </g>
                            );
                          })}

                        {/* Annotation row: "Not at all" ←→ measure name ←→ "The most" */}
                        {(() => {
                          const adaptiveMaxChars =
                            chartW < 350
                              ? 20
                              : chartW < 600
                                ? 36
                                : chartW < 800
                                  ? 48
                                  : 60;
                          const lines = splitLabel(
                            displayName,
                            adaptiveMaxChars
                          );
                          const midX = PAD_LEFT + chartW / 2;
                          const baseY = PAD_TOP + chartH + 36;
                          const line2Y = baseY + 16;
                          const arrowY = baseY - 5;

                          const charW = FS * 0.58;
                          const leftHW = ('Not at all'.length * charW) / 2;
                          const rightHW = ('The most'.length * charW) / 2;
                          const nameHW =
                            (Math.max(...lines.map((l) => l.length)) * charW) /
                            2;

                          const leftLineX1 = midX - nameHW - 14;
                          const leftLineX2 = PAD_LEFT + leftHW * 2 + 10;
                          const rightLineX1 = midX + nameHW + 14;
                          const rightLineX2 =
                            PAD_LEFT + chartW - rightHW * 2 - 10;
                          const minLineLen = 12;

                          const minRoom = Math.min(
                            leftLineX1 - leftLineX2,
                            rightLineX2 - rightLineX1
                          );
                          const annotWeight = minRoom >= 22 ? 700 : 400;
                          const annotSize = minRoom >= -10 ? FS + 1 : FS - 1;

                          return (
                            <>
                              <text
                                x={PAD_LEFT}
                                y={baseY}
                                textAnchor="start"
                                fontSize={annotSize}
                                fontWeight={annotWeight}
                                fill={LABEL_COLOR}
                              >
                                {t('charts.not_at_all')}
                              </text>
                              {leftLineX1 - leftLineX2 > minLineLen && (
                                <line
                                  x1={leftLineX2}
                                  y1={arrowY}
                                  x2={leftLineX1}
                                  y2={arrowY}
                                  stroke={AXIS_COLOR}
                                  strokeWidth={1}
                                  markerEnd="url(#or-arrowHead)"
                                />
                              )}
                              <text
                                x={midX}
                                y={baseY}
                                textAnchor="middle"
                                fontSize={annotSize}
                                fontWeight={annotWeight}
                                fill={LABEL_COLOR}
                              >
                                {lines[0]}
                              </text>
                              {lines[1] && (
                                <text
                                  x={midX}
                                  y={line2Y}
                                  textAnchor="middle"
                                  fontSize={annotSize}
                                  fontWeight={annotWeight}
                                  fill={LABEL_COLOR}
                                >
                                  {lines[1]}
                                </text>
                              )}
                              {rightLineX2 - rightLineX1 > minLineLen && (
                                <line
                                  x1={rightLineX1}
                                  y1={arrowY}
                                  x2={rightLineX2}
                                  y2={arrowY}
                                  stroke={AXIS_COLOR}
                                  strokeWidth={1}
                                  markerEnd="url(#or-arrowHead)"
                                />
                              )}
                              <text
                                x={PAD_LEFT + chartW}
                                y={baseY}
                                textAnchor="end"
                                fontSize={annotSize}
                                fontWeight={annotWeight}
                                fill={LABEL_COLOR}
                              >
                                {t('charts.the_most')}
                              </text>
                            </>
                          );
                        })()}

                        {/* Y axis label + silhouette — trend and aggregate charts */}
                        {(chartMode === 'trend' || chartMode === 'aggregate') &&
                          (() => {
                            const yLbl = hasThirdParty
                              ? t(
                                  'charts.outcome_ratings.participants_in_evaluation'
                                )
                              : t('charts.outcome_ratings.number_of_responses');
                            const charAdv = (FS + 1) * 0.58 * 1.06;
                            const lblHalf = (yLbl.length * charAdv) / 2;
                            const silOffset = Math.min(
                              Math.floor(chartH / 2) - 20,
                              Math.ceil(lblHalf + 15 + 7)
                            );
                            return (
                              <g
                                transform={`translate(${Y_COL_X}, ${
                                  PAD_TOP + chartH / 2
                                })`}
                                fill={LABEL_COLOR}
                              >
                                <text
                                  transform="rotate(-90)"
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  fontSize={FS + 1}
                                  fontWeight={700}
                                  letterSpacing="0.06em"
                                >
                                  {yLbl}
                                </text>
                                <g
                                  transform={`translate(0, ${-silOffset}) scale(0.85)`}
                                >
                                  <circle cx="0" cy="-19" r="4.5" />
                                  <path d="M -6 -13 L 6 -13 L 5 -5 L -5 -5 Z" />
                                  <path d="M -6 -13 L -10 -6 L -8 -5 L -5 -11 Z" />
                                  <path d="M 6 -13 L 10 -6 L 8 -5 L 5 -11 Z" />
                                  <path d="M -5 -5 L -6 8 L -2 8 L 0 0 L 2 8 L 6 8 L 5 -5 Z" />
                                </g>
                              </g>
                            );
                          })()}
                      </svg>
                    </div>
                    {/* end measurement div */}

                    {/* Prose summary */}
                    {prose && prose.length > 0 && (
                      <div className="or__prose">
                        <ul className="or__prose-list">
                          {prose.map((p, i) => (
                            <li key={i} className="or__prose-text">
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
                {/* end chart + prose gate */}

                {/* ── Activities included — collapsible checkbox list ── */}
                {!embedView && (
                  <div className="or__activities">
                    {/* Header row: toggle + All / None controls */}
                    <div className="or__activities-header">
                      <button
                        onClick={() => setListOpen((o) => !o)}
                        className="or__activities-toggle"
                      >
                        <span className="or__activities-toggle-label">
                          {t('charts.outcome_ratings.activities_included')}
                        </span>
                        <span className="or__activities-toggle-caret">
                          {listOpen ? '▾' : '▸'}
                        </span>
                      </button>
                      {listOpen && (
                        <span className="or__activities-allnone">
                          <button
                            onClick={() =>
                              setSelectedActivities(
                                new Set(activities.map((a) => a.id))
                              )
                            }
                            className="or__link-btn"
                          >
                            {t('common.all').toLowerCase()}
                          </button>
                          {' / '}
                          <button
                            onClick={() => setSelectedActivities(new Set())}
                            className="or__link-btn"
                          >
                            {t('common.none').toLowerCase()}
                          </button>
                        </span>
                      )}
                    </div>

                    {listOpen && (
                      <div className="or__activity-list">
                        {activities.map((a) => {
                          const checked = selectedActivities.has(a.id);
                          const toggle = () =>
                            setSelectedActivities((prev) => {
                              const next = new Set(prev);
                              if (checked) next.delete(a.id);
                              else next.add(a.id);
                              return next;
                            });
                          return (
                            <label key={a.id} className="or__activity-row">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={toggle}
                                className="or__activity-check"
                                style={{
                                  accentColor: `rgb(${BASE_COLOR})`,
                                }}
                              />
                              <span
                                className={`or__activity-name${
                                  checked ? ' or__activity-name--checked' : ''
                                }`}
                              >
                                {a.name}
                              </span>
                              <span className="or__activity-meta">
                                {t('charts.num_respondents', {
                                  count: a.respondents,
                                  countFmt: fmtN(a.respondents),
                                })}
                                {a.avgRating != null &&
                                  ` · ${t('charts.average_abbrev')} ${a.avgRating}`}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          {/* end capture area */}
        </>
      )}
      {/* end collapsible body */}
    </div>
  );
});

export default OutcomeRatings;
